import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { getSummary } from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// IntegrationsStatusWidget
// ---------------------------------------------------------------- #
//
// The Integrations block pinned to the bottom of the sidebar: the word
// Integrations with "40 of 48" (connected of expected park-product slots)
// on the right, and a thin progress bar under it: green for connected,
// red for slots that need action, grey for the rest. Hovering or focusing it
// opens a small card listing each product's status. The block links to /integrations.
//
// It loads GET summary on mount and again whenever the page fires the
// "parki:integrations-changed" event. It renders nothing unless the user
// is an Entity Admin, and nothing while loading or after an error.
//
// Props:
//   user     The current user; only Entity Admins see the widget
//   compact  True when the sidebar is collapsed: a plug icon with one coloured dot

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** Event the Integrations page fires after a save. */
const INTEGRATIONS_CHANGED_EVENT = 'parki:integrations-changed';

/** The six products in display order, for the hover text. */
const WIDGET_PRODUCTS = [
  { key: 'pms', label: 'PMS' },
  { key: 'bookkeeping', label: 'Bookkeeping' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'business', label: 'Business' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' }
];

/** Gap in pixels between the widget and its popover. */
const POPOVER_OFFSET = 10;

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * Treat true, 1 and "1" as yes, because the profile API may send the flag as any of them.
 */
const isYesFlag = (value) => {
  return value === true || value === 1 || value === '1';
};

/**
 * Work out one product's row in the popover: a short status, its dot colour and a count.
 * For example { status: '1 needs action', tone: 'is-action', count: '0 of 1' }.
 */
const buildProductRow = (label, counts) => {
  // No figures for this product
  if (!counts) {
    return { label, status: 'No information', tone: 'is-muted', count: '' };
  }

  // Not part of the plan for any park
  if (counts.inPlan === 0) {
    return { label, status: 'Not in plan', tone: 'is-muted', count: '' };
  }

  // In the plan, but every park is marked not needed
  const parksExpected = counts.inPlan - counts.notNeeded;
  if (parksExpected <= 0) {
    return { label, status: 'Not needed', tone: 'is-muted', count: '' };
  }

  // Pick the status that most needs attention: needs action, then not set up, then in review
  const count = `${counts.complete} of ${parksExpected}`;
  if (counts.needsAction > 0) {
    let actionWord = 'need action';
    if (counts.needsAction === 1) {
      actionWord = 'needs action';
    }
    return { label, status: `${counts.needsAction} ${actionWord}`, tone: 'is-action', count };
  }
  if (counts.gap > 0) {
    return { label, status: `${counts.gap} not set up`, tone: 'is-waiting', count };
  }
  if (counts.inReview > 0) {
    return { label, status: `${counts.inReview} in review`, tone: 'is-waiting', count };
  }

  // Everything expected is connected
  return { label, status: 'Connected', tone: 'is-complete', count };
};

/**
 * Turn one popover row into a plain sentence for screen readers.
 */
const describeProductRow = (row) => {
  if (row.count === '') {
    return `${row.label}: ${row.status}.`;
  }
  return `${row.label}: ${row.count} connected, ${row.status}.`;
};

/**
 * Build the line under the icons, for example "3 gaps, 1 needs action", or "All set".
 */
const buildTotalsLine = (totals) => {
  const gapCount = totals?.gap || 0;
  const needsActionCount = totals?.needsAction || 0;

  // Nothing to do
  if (gapCount === 0 && needsActionCount === 0) {
    return 'All set';
  }

  // Name each count that is above zero
  const parts = [];
  if (gapCount > 0) {
    let gapWord = 'gaps';
    if (gapCount === 1) {
      gapWord = 'gap';
    }
    parts.push(`${gapCount} ${gapWord}`);
  }
  if (needsActionCount > 0) {
    let actionWord = 'need action';
    if (needsActionCount === 1) {
      actionWord = 'needs action';
    }
    parts.push(`${needsActionCount} ${actionWord}`);
  }
  return parts.join(' · ');
};

// ---------------------------------------------------------------- #
// The widget
// ---------------------------------------------------------------- #

/**
 * The sidebar block showing the Integrations Register status at a glance.
 */
const IntegrationsStatusWidget = ({ user, compact = false }) => {
  const [summary, setSummary] = useState(null);

  // Where the popover sits on screen while the widget is hovered or focused, or null when hidden
  const [popoverPosition, setPopoverPosition] = useState(null);

  // Only Entity Admins see the widget
  const isEntityAdmin = isYesFlag(user?.isEntityAdmin);

  // ---------------------------------------------------------------- #
  // Load the summary
  // ---------------------------------------------------------------- #

  // Load on mount and again whenever the register changes
  useEffect(() => {
    if (!isEntityAdmin) {
      return;
    }

    let cancelled = false;
    const loadSummary = async () => {
      try {
        const result = await getSummary();
        if (!cancelled) {
          setSummary(result.data);
        }
      } catch (err) {
        // The widget simply stays hidden when the summary cannot be loaded
        if (!cancelled) {
          setSummary(null);
        }
      }
    };

    loadSummary();
    window.addEventListener(INTEGRATIONS_CHANGED_EVENT, loadSummary);

    return () => {
      cancelled = true;
      window.removeEventListener(INTEGRATIONS_CHANGED_EVENT, loadSummary);
    };
  }, [isEntityAdmin]);

  // Hide the popover if the page scrolls or resizes, so it never floats away from the widget
  useEffect(() => {
    if (!popoverPosition) {
      return;
    }
    const hidePopover = () => {
      setPopoverPosition(null);
    };
    window.addEventListener('scroll', hidePopover, true);
    window.addEventListener('resize', hidePopover);
    return () => {
      window.removeEventListener('scroll', hidePopover, true);
      window.removeEventListener('resize', hidePopover);
    };
  }, [popoverPosition]);

  // Nothing for non-admins, and nothing while loading
  if (!isEntityAdmin || !summary) {
    return null;
  }

  // ---------------------------------------------------------------- #
  // Add up the progress and build the popover rows
  // ---------------------------------------------------------------- #

  // Across the six products: slots connected, slots expected (in plan and needed), slots needing action
  let connectedCount = 0;
  let expectedCount = 0;
  let needsActionCount = 0;
  const productRows = [];
  for (const product of WIDGET_PRODUCTS) {
    const counts = summary.products?.[product.key];
    productRows.push(buildProductRow(product.label, counts));
    if (!counts) {
      continue;
    }
    connectedCount += counts.complete;
    expectedCount += counts.inPlan - counts.notNeeded;
    needsActionCount += counts.needsAction;
  }

  // The popover's headline and the line under it, for example "2 gaps · 4 need action"
  const headline = `${connectedCount} of ${expectedCount} connected`;
  const totalsLine = buildTotalsLine(summary.totals);

  // The same information as plain sentences, for screen readers
  const spokenLines = [`Integrations: ${headline}. ${totalsLine}.`];
  for (const row of productRows) {
    spokenLines.push(describeProductRow(row));
  }
  const spokenSummary = spokenLines.join(' ');

  // ---------------------------------------------------------------- #
  // Show and hide the popover
  // ---------------------------------------------------------------- #

  /**
   * Place the popover next to the widget: above it in the full sidebar, to its right when collapsed.
   */
  const showPopover = (event) => {
    const widgetBox = event.currentTarget.getBoundingClientRect();

    // Collapsed sidebar: to the right of the icon, vertically centred on it
    if (compact) {
      setPopoverPosition({
        placement: 'right',
        left: widgetBox.right + POPOVER_OFFSET,
        top: widgetBox.top + widgetBox.height / 2
      });
      return;
    }

    // Full sidebar: above the widget, lined up with its left edge
    setPopoverPosition({
      placement: 'top',
      left: widgetBox.left,
      top: widgetBox.top - POPOVER_OFFSET
    });
  };

  /**
   * Hide the popover.
   */
  const hidePopover = () => {
    setPopoverPosition(null);
  };

  // The popover card, drawn on the page body so the sidebar's overflow cannot clip it
  let popover = null;
  if (popoverPosition) {
    const popoverStyle = {
      left: `${popoverPosition.left}px`,
      top: `${popoverPosition.top}px`
    };
    popover = createPortal(
      <div
        className={`integrations-widget-popover is-${popoverPosition.placement}`}
        style={popoverStyle}
        aria-hidden="true"
      >
        {/* Headline and totals */}
        <div className="integrations-widget-popover-headline">{headline}</div>
        <div className="integrations-widget-popover-totals">{totalsLine}</div>

        {/* One row per product: dot, name, status, count */}
        <ul className="integrations-widget-popover-list">
          {productRows.map((row) => (
            <li key={row.label} className="integrations-widget-popover-row">
              <span className={`integrations-widget-popover-dot ${row.tone}`}></span>
              <span className="integrations-widget-popover-label">{row.label}</span>
              <span className={`integrations-widget-popover-status ${row.tone}`}>{row.status}</span>
              <span className="integrations-widget-popover-count">{row.count}</span>
            </li>
          ))}
        </ul>
      </div>,
      document.body
    );
  }

  // Bar widths as percentages of the expected slots
  let connectedPercent = 0;
  let needsActionPercent = 0;
  const hasExpectedSlots = expectedCount > 0;
  if (hasExpectedSlots) {
    connectedPercent = Math.round((connectedCount / expectedCount) * 100);
    needsActionPercent = Math.round((needsActionCount / expectedCount) * 100);
  }

  // ---------------------------------------------------------------- #
  // Render: compact (collapsed sidebar)
  // ---------------------------------------------------------------- #

  if (compact) {
    // One dot: red when anything needs action, amber when anything is left to set up, else green
    const gapCount = summary.totals?.gap || 0;
    let dotColour = 'success';
    if (needsActionCount > 0) {
      dotColour = 'danger';
    } else if (gapCount > 0) {
      dotColour = 'warning';
    }

    return (
      <>
        <Link
          to="/integrations"
          className="integrations-widget is-compact"
          onMouseEnter={showPopover}
          onMouseLeave={hidePopover}
          onFocus={showPopover}
          onBlur={hidePopover}
        >
          <i className="icon-base ti tabler-plug-connected integrations-widget-icon"></i>
          <span className={`integrations-widget-dot bg-${dotColour}`} aria-hidden="true"></span>
          <span className="visually-hidden">{spokenSummary}</span>
        </Link>
        {popover}
      </>
    );
  }

  // ---------------------------------------------------------------- #
  // Render: full
  // ---------------------------------------------------------------- #

  return (
    <>
      <Link
        to="/integrations"
        className="integrations-widget"
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocus={showPopover}
        onBlur={hidePopover}
      >
        <span className="integrations-widget-heading">
          <span>Integrations</span>
          <span className="integrations-widget-count">{connectedCount} of {expectedCount}</span>
        </span>
        <span className="integrations-widget-bar" aria-hidden="true">
          <span className="integrations-widget-bar-connected" style={{ width: `${connectedPercent}%` }}></span>
          <span className="integrations-widget-bar-action" style={{ width: `${needsActionPercent}%` }}></span>
        </span>
        <span className="visually-hidden">{spokenSummary}</span>
      </Link>
      {popover}
    </>
  );
};

export default IntegrationsStatusWidget;
